/**
 * useInvoices — Settings → Billing invoice list reader.
 *
 * signup-billing-mvp Phase 6 Session 4 (2026-05-13; envelope-fix 2026-05-15).
 *
 * Reads `GET /api/billing/invoices`. The server route passes the bare
 * `InvoiceSummary[]` array directly into `sendSuccess`, so the wire shape is
 * `{ success: true, data: InvoiceSummary[], meta: {...} }` — ONE level of
 * unwrap, not two. (Pre-fix this hook unwrapped twice via a misread of the
 * `InvoicesResponse` Zod schema's documented shape, returning `undefined` →
 * falling back to `[]` → `<BillingTab>` showed "No invoices yet" even when
 * Stripe had 4 paid invoices on the customer. Surfaced in walk #5,
 * 2026-05-15.)
 *
 * No-Stripe-customer users get `[]` (the API never returns 404 for that
 * case — research § 4.8 / Phase 5 Session 6). The hook is enabled only when
 * `useAuth().user` is present; admin callers don't reach this hook because
 * `<BillingTab>` short-circuits on `isAdmin` before mount.
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/use-auth';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import type { InvoiceSummary } from '@arcanum-hq/shared-types';

interface InvoicesEnvelope {
  success: boolean;
  data: InvoiceSummary[];
}

export function useInvoices() {
  const { user } = useAuth();
  const query = useQuery<InvoiceSummary[]>({
    queryKey: queryKeys.billing.invoices(user?.id),
    queryFn: async () => {
      const envelope = await api.get<InvoicesEnvelope>('/api/billing/invoices');
      return envelope.data;
    },
    enabled: !!user,
    staleTime: 60_000,
  });

  return {
    invoices: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
